/**
 * A draft is working material. Issuing it allocates a serial that is never
 * reused and freezes snapshots of the business and the customer, because from
 * that moment it is an official document rather than a screen.
 */

export const ReceiptStatus = {
  Draft: 'draft',
  Issued: 'issued',
  Cancelled: 'cancelled'
} as const;

export type ReceiptStatus = (typeof ReceiptStatus)[keyof typeof ReceiptStatus];

export const PAYMENT_METHODS = [
  'cash', 'bank_transfer', 'credit_card', 'bit', 'paybox', 'check', 'other'
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * What the tutor is, for tax. A עוסק פטור hands over a קבלה; a עוסק מורשה or
 * a company hands over a חשבונית מס-קבלה carrying a VAT line. The server
 * decides which from this, so the screen never picks a document type itself.
 */
export const BUSINESS_TYPES = ['osek_patur', 'osek_murshe', 'company', 'amuta'] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const DOCUMENT_TYPES = ['receipt', 'tax_invoice_receipt'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/** A cheque is five details, not a note. This is one tender on one receipt. */
export interface ReceiptPaymentModel {
  id?: number;
  method: PaymentMethod;
  amount: string;
  bankNumber?: string | null;
  branchNumber?: string | null;
  accountNumber?: string | null;
  chequeNumber?: string | null;
  dueDate?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  reference?: string | null;
}

/** What the business still has to fill in before it can issue legally. */
export interface ComplianceStatusModel {
  ready: boolean;
  missing: string[];
  businessType: BusinessType;
  documentType: DocumentType;
  /** Basis points. 1800 is 18%. Zero when the business charges no VAT. */
  vatRate: number;
}

/**
 * §2.6 of the uniform-structure instructions: the report an auditor holds
 * beside the file. Every document code in appendix 1 appears, including the
 * ones this app never issues, which carry a zero.
 */
export interface OpenFormatSummaryModel {
  from: string;
  to: string;
  documents: { code: string; name: string; count: number; totalAgorot: number }[];
  cancelledCount: number;
  records: { code: string; count: number }[];
  totalRecords: number;
}

/**
 * One production of the uniform-structure files: the archive, and what §5.4
 * has the summary screen say about it. `records` holds only the record types
 * the file actually contains, which is the rule for that screen's table.
 */
export interface OpenFormatRunModel {
  blob: Blob;
  from: string;
  to: string;
  /** `OPENFRMT\<8 digits>.<YY>\<MMDDhhmm>`, as the archive unpacks. */
  path: string;
  softwareName: string;
  records: { code: string; count: number }[];
}

export interface ReceiptItemModel {
  id: number;
  paymentId: number | null;
  description: string | null;
  date: string | null;
  amount: string;
}

export interface ReceiptModel {
  id: number;
  receiptNumber: string | null;
  fiscalYear: number | null;
  docType: DocumentType;
  status: ReceiptStatus;
  currency: string;
  amount: string;
  customerName: string | null;
  student: string | null;
  issueDate: string | null;
  paymentDate: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  description: string | null;
  notes: string | null;
  customerNotes: string | null;
  customerAddress: string | null;
  customerTaxId: string | null;
  /** Basis points, frozen at issue. Null on a draft. */
  vatRate: number | null;
  /** The money split in agorot, frozen at issue. gross is always amount*100. */
  grossAgorot: number | null;
  netAgorot: number | null;
  vatAgorot: number | null;
  customerSnapshot: { name: string | null; student: string | null; type: string } | null;
  businessSnapshot: BusinessSettingsModel | null;
  templateSnapshot: {
    language: string; printSize: string; footerText: string | null;
    signatureName: string | null; logo: string | null;
  } | null;
  autoCreated: boolean;
  issuedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  /** Set the first time the document left the screen. Later prints are copies. */
  originalPrintedAt: string | null;
  copyCount: number;
  createdAt: string;
  updatedAt: string;
  items?: ReceiptItemModel[];
  payments?: ReceiptPaymentModel[];
}

export interface BusinessSettingsModel {
  businessName: string | null;
  registrationNumber: string | null;
  businessType: BusinessType;
  address: string | null;
  addressStreet: string | null;
  addressHouse: string | null;
  addressCity: string | null;
  addressZip: string | null;
  softwareRegistryNumber: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logo: string | null;
  defaultCurrency: string;
  defaultLanguage: string;
  numberPrefix: string;
  numberStart: number;
  numberLength: number;
  yearPrefix: boolean;
  perYearNumbering: boolean;
  footerText: string | null;
  signatureName: string | null;
  /** Display only. Absent from snapshots frozen before these existed. */
  businessNameEn?: string | null;
  addressEn?: string | null;
  signatureNameEn?: string | null;
  footerTextEn?: string | null;
  printSize: string;
  /** True once a document carries a number: the run can no longer be changed. */
  numberingLocked: boolean;
}

export interface BulkIssueResultModel {
  count: number;
  total: number;
  receipts: ReceiptModel[];
}

export interface UnreportedSummaryModel {
  count: number;
  total: number;
  students: {
    student: string;
    count: number;
    total: number;
    lessons: { id: number; date: string; amount: string }[];
  }[];
}

export interface ReceiptStatsModel {
  monthIncome: number;
  monthCount: number;
  issuedCount: number;
  issuedTotal: number;
  averageAmount: number;
  cancelledCount: number;
  draftCount: number;
  unreportedCount: number;
  unreportedTotal: number;
  lastReceiptNumber: string | null;
  recent: {
    id: number; receiptNumber: string | null; status: ReceiptStatus;
    amount: string; customerName: string | null; issueDate: string | null;
  }[];
}
